import 'core-js/stable'
import 'regenerator-runtime/runtime'

import { Provider } from 'react-redux'
import React from 'react'
import moment from 'moment'
import { render } from 'react-dom'

import { SORT_OPTIONS } from './settings'
import { gettext } from '@bikemap/js/services'
import { findOptimalPageSize, isMapRendered, setMapViewportToUserLocation } from './helpers'
import { hydrateFromQueryParams, initHistory } from './history'
import { updatePageSize, updateSort } from '../../integrations/list'
import { DebugModeActionItem, RouteSearch } from './components/container'
import initSearchMap from './map'
import configureStore from './store'
import { DEFAULT_PAGE_SIZE, LANGUAGE } from '../../settings'
import { TranslationProvider } from '@bikemap/components'
import initGlobal, { initPageFooter, initPageHeader } from '@bikemap/js/integrations/global'
import { ConnectedLocationSearch } from '@bikemap/js/integrations/geolocation'
import { DebugStateActionItem } from '@bikemap/js/integrations/debugging'
import { initialBoundlessQuery } from './actions'

moment.locale(LANGUAGE)

/**
 * Make the search app optimize the results page size to fill the viewport.
 * @param {import("redux").Store} store
 */
function initPageSizeOptimization(store) {
    const { isLegacyLink } = store.getState()

    // Set default page size for backward compatibility on legacy links
    if (isLegacyLink) {
        store.dispatch(updatePageSize(DEFAULT_PAGE_SIZE))
        return
    }

    // Optimize page size now
    optimizePageSize(store)

    // Re-evaluate page size on window resizes
    window.addEventListener('resize', () => { optimizePageSize(store) })

    // Re-evaluate page size on page changes
    let currentPage
    store.subscribe(() => {
        const { results } = store.getState()
        if (results.page !== currentPage) {
            currentPage = results.page
            optimizePageSize(store)
        }
    })
}

/**
 * Determine a good page size for the current viewport dimensions (assuming no special zoom) and
 * update the state if necessary.
 * @param {import("redux").Store} store
 */
function optimizePageSize(store) {
    const { results } = store.getState()

    // Don't optimize on higher pages to keep shared links the same and avoid unexpected changes
    if (results.page !== 1) return

    const optimalPageSize = findOptimalPageSize()
    if (optimalPageSize !== results.pageSize) {
        store.dispatch(updatePageSize(optimalPageSize))
    }
}

/**
 * Initialize the route search app. This includes all bootstrapping and bindings.
 */
async function init() {
    const store = configureStore()

    initGlobal()
    initPageHeader({
        renderPageHeaderContent: () => (
            <Provider store={store}><ConnectedLocationSearch /></Provider>
        ),
        renderStaffActions: () => (
            <Provider store={store}>
                <DebugModeActionItem />
                <DebugStateActionItem collectState={() => store.getState()} />
            </Provider>
        ),
    })
    initPageFooter()

    // Set default sort for results
    const { sort, order } = SORT_OPTIONS[0]
    store.dispatch(updateSort(sort, order))

    // User location should be the default map viewport
    await setMapViewportToUserLocation(store)

    // Override default state with data from URL query params
    hydrateFromQueryParams(store)

    // Render UI components
    const searchContainer = document.getElementById('search-container')
    render(
        (
            <Provider store={store}>
                <TranslationProvider translate={gettext}>
                    <RouteSearch />
                </TranslationProvider>
            </Provider>
        ),
        searchContainer
    )

    // Optimize page size if possible
    initPageSizeOptimization(store)

    // Initialize updating the URL when the state changes
    initHistory(store)

    // The map container is only visible on large screens
    if (isMapRendered()) {
        // Large screen
        initSearchMap(store)
    } else {
        // Small screen
        const lazyMapInit = () => {
            const { isMapView } = store.getState()
            if (isMapRendered() || isMapView) {
                initSearchMap(store)
                window.removeEventListener('resize', lazyMapInit)
                removeStateListener()
            }
        }
        window.addEventListener('resize', lazyMapInit)
        const removeStateListener = store.subscribe(lazyMapInit)

        // Make sure routes are requested immediately
        store.dispatch(initialBoundlessQuery())
    }
}

init()
